import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { cn } from '@/lib/utils';

interface HoverEffectItem {
  title: string;
  description: string;
  link?: string;
  icon?: React.ReactNode;
}

interface HoverEffectProps {
  items: HoverEffectItem[];
  className?: string;
}

/**
 * R2 - Reusable card grid with smooth hover effects
 * R3 - Accepts array of items with title, description, and link
 * R4 - Responsive grid layout (1 col mobile, 2 tablet, 3 desktop)
 * 
 * INBOCA Branding: Light mode with subtle teal hover effect
 * 
 * Usage:
 * <HoverEffect items={[{ title: 'Service', description: '...', link: '/service' }]} />
 */
export function HoverEffect({ items, className }: HoverEffectProps) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  return (
    <div
      className={cn(
        'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 py-10',
        className
      )}
    >
      {items.map((item, idx) => {
        const Wrapper = item.link ? 'a' : 'div';
        const wrapperProps = item.link ? { href: item.link } : {};
        return (
        <Wrapper
          {...wrapperProps}
          key={item.title + idx}
          className="relative group block p-2 h-full w-full"
          onMouseEnter={() => setHoveredIndex(idx)}
          onMouseLeave={() => setHoveredIndex(null)}
        >
          <AnimatePresence>
            {hoveredIndex === idx && (
              <motion.span
                className="absolute inset-0 h-full w-full bg-inboca-teal/5 block rounded-3xl"
                layoutId="hoverBackground"
                initial={{ opacity: 0 }}
                animate={{
                  opacity: 1,
                  transition: { duration: 0.15 },
                }}
                exit={{
                  opacity: 0,
                  transition: { duration: 0.15, delay: 0.2 },
                }}
              />
            )}
          </AnimatePresence>
          <Card>
            {item.icon && (
              <div className="mb-2 text-inboca-teal">
                {item.icon}
              </div>
            )}
            <CardTitle>{item.title}</CardTitle>
            <CardDescription>{item.description}</CardDescription>
          </Card>
        </Wrapper>
        );
      })}
    </div>
  );
}

export function Card({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        'rounded-2xl h-full w-full p-4 overflow-hidden bg-white border border-gray-200 group-hover:border-inboca-teal/30 transition-colors relative z-20 shadow-sm',
        className
      )}
    >
      <div className="relative z-50">
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

export function CardTitle({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <h4 className={cn('text-gray-800 font-bold tracking-wide mt-4', className)}>
      {children}
    </h4>
  );
}

export function CardDescription({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <p
      className={cn(
        'mt-4 text-gray-600 tracking-wide leading-relaxed text-sm',
        className
      )}
    >
      {children}
    </p>
  );
}
